// TODO: Remove this adapter when Personal Details components (PersonalInformation, PIVInformation, OtherDetails)
// are migrated from useWrappedRequest to useQuery/useMutation. At that point, use useQuery directly in the context.
import { useInvestmentJoinAndAllocationController } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/useInvestmentJoinAndAllocationController';
import { getInvestorData } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

type ContextType = ReturnType<typeof useInvestmentJoinAndAllocationController>;

/**
 * Adapts a useQuery result into the WrapRequest shape expected by the shared
 * Personal Details components (PersonalInformation, PIVInformation, OtherDetails).
 *
 * These components depend on the deprecated useWrappedRequest interface (.fetch(), .concludeWithPromise()).
 * This adapter avoids importing useWrappedRequest while maintaining compatibility.
 * Remove this once the Personal Details components are migrated to useQuery/useMutation.
 */
export const useInvestorDataAdapter = (kycWorkflowId?: string) => {
	const investorQuery = useQuery({
		queryKey: [
			API_PATHS.SHAREHOLDER_INVESTOR_GET,
			{ withDraftPersonRelations: true, kycWorkflowId: kycWorkflowId ?? '' },
		],
		queryFn: () => getInvestorData({ withDraftPersonRelations: true, kycWorkflowId }),
	});

	const fetch = useCallback(
		(_args?: { withDraftPersonRelations?: boolean }) => {
			const refetchPromise = investorQuery.refetch().then((r) => r.data);
			return {
				concludeWithPromise: () => refetchPromise,
				onSuccess: (cb: (data: typeof investorQuery.data) => void) => {
					void refetchPromise.then((data) => {
						if (data) {
							cb(data);
						}
					});
					return {
						concludeWithPromise: () => refetchPromise,
						onFinish: () => undefined,
						onLocalError: () => undefined,
						onGlobalError: () => undefined,
						onAnyError: () => undefined,
						onUnsafeFinish: () => undefined,
						onComponentUnmountedAndRequestFinished: () => undefined,
						onPromiseCancel: () => undefined,
					};
				},
				onFinish: () => undefined,
				onLocalError: () => undefined,
				onGlobalError: () => undefined,
				onAnyError: () => undefined,
				onUnsafeFinish: () => undefined,
				onComponentUnmountedAndRequestFinished: () => undefined,
				onPromiseCancel: () => undefined,
			};
		},
		[investorQuery.refetch]
	);

	return {
		isLoading: investorQuery.isLoading,
		isError: investorQuery.isError,
		response: investorQuery.data ?? null,
		fetch,
		abortController: new AbortController(),
		maybeAbort: () => undefined,
		resetInstance: () => undefined,
	} as unknown as ContextType['getInvestorDataRequest'];
};
